/**
 * Image Proxy Utility
 * Provides multiple fallback options for displaying AI generated images
 */

class ImageProxy {
  constructor() {
    // List of CORS proxy services (for development only)
    this.proxyServices = [
      'https://cors-anywhere.herokuapp.com/',
      'https://api.allorigins.win/raw?url=',
      'https://corsproxy.io/?'
    ];
  }

  /**
   * Try to fetch image and convert to base64
   * @param {string} imageUrl - The image URL to fetch
   * @returns {Promise<string>} - Base64 data URL or original URL
   */
  async fetchImageAsBase64(imageUrl) {
    try {
      const response = await fetch(imageUrl, {
        mode: 'cors',
        headers: {
          'Accept': 'image/*'
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        return await this.blobToBase64(blob);
      }
    } catch (error) {
      console.warn('Direct fetch failed:', error);
    }
    
    return imageUrl; // Return original URL as fallback
  }

  /**
   * Convert blob to base64
   * @param {Blob} blob - The blob to convert
   * @returns {Promise<string>} - Base64 data URL
   */
  blobToBase64(blob) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Try to fetch image through a proxy
   * @param {string} imageUrl - The image URL to fetch
   * @returns {Promise<string>} - Base64 data URL or original URL
   */
  async fetchImageWithProxy(imageUrl) {
    for (const proxy of this.proxyServices) {
      try {
        const proxyUrl = proxy + encodeURIComponent(imageUrl);
        const response = await fetch(proxyUrl, {
          headers: {
            'Accept': 'image/*'
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          return await this.blobToBase64(blob);
        }
      } catch (error) {
        console.warn(`Proxy ${proxy} failed:`, error);
        continue;
      }
    }
    
    return imageUrl; // Return original URL if all proxies fail
  }

  /**
   * Get image URL with fallback handling
   * @param {string} imageUrl - The image URL
   * @param {boolean} useProxy - Whether to try proxy services
   * @returns {Promise<string>} - Processed image URL
   */
  async getImageUrl(imageUrl, useProxy = false) {
    if (!imageUrl) return null;

    // Try direct fetch first
    const base64Url = await this.fetchImageAsBase64(imageUrl);
    if (base64Url !== imageUrl) {
      return base64Url; // Successfully converted to base64
    }

    // If direct fetch failed and proxy is enabled, try proxy
    if (useProxy) {
      return await this.fetchImageWithProxy(imageUrl);
    }

    // Return original URL as fallback
    return imageUrl;
  }

  /**
   * Create a fallback display for CORS-blocked images
   * @param {string} imageUrl - The original image URL
   * @returns {string} - HTML string for fallback display
   */
  createFallbackDisplay(imageUrl) {
    return `
      <div class="text-center p-4 bg-gray-100 rounded-lg">
        <div class="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <svg class="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
        </div>
        <p class="text-sm font-medium text-gray-700 mb-1">Image Generated Successfully</p>
        <p class="text-xs text-gray-500 mb-2">Preview not available due to CORS restrictions</p>
        <p class="text-xs text-gray-500">The image will be saved when you create the screen</p>
        <a href="${imageUrl}" target="_blank" class="text-xs text-primary-600 hover:text-primary-700 underline">
          View Image in New Tab
        </a>
      </div>
    `;
  }
}

export default new ImageProxy(); 